// The student Progress page's Chapters table, or Sections on a chapter view, on the full-page table's
// embedded mode: every row in book order, no toolbar, sort or pager. On the course view a chapter row
// expands in place to its Sections rows, drawn in the same columns. The rows come from
// course/views/progress.py chapters_table_row, and an expanded chapter's from its sections view.

import React from 'react'
import PropTypes from 'prop-types'

import { MeTable } from '@CommonComponents'
import { client } from '../../utils'
import { openProgressDialog } from '../../legacy/course/progress'

// An empty value: a dash on screen, "No data" to a screen reader, after any context it needs
const NoData = ({ context = '' }) => (
  <span className="progress-nodata">
    <span aria-hidden="true">–</span>
    <span className="sr-only">{`${context}No data`}</span>
  </span>
)

NoData.propTypes = { context: PropTypes.string }

// A metric cell: "41 of 50" opening the row's detail dialog, over the 4px cell meter, with a check
// when this metric is finished; a dash when the row has nothing to count.
export const MetricCell = ({ row, metric }) => {
  const value = row.metrics[metric.key]
  if (!value || !value.total)
    return <NoData context={`${metric.label} for ${row.short}, `} />
  const open = event =>
    openProgressDialog({
      id: 'analytics-modal',
      url: value.url,
      title: `${metric.label}: ${row.dialogTitle}`,
      trigger: event.currentTarget,
    })
  return (
    <>
      <button
        type="button"
        className="me-btn-link-inline progress-cell-link"
        aria-haspopup="dialog"
        aria-label={`${metric.label} for ${row.short}, ${value.done} of ${value.total} ${value.noun}, open details`}
        onClick={open}
      >
        {value.done} of {value.total}
      </button>
      {value.finished && (
        <i
          className="fa-solid fa-circle-check progress-finished progress-finished-cell"
          aria-hidden="true"
        />
      )}
      <div
        className={`progress-meter progress-meter-cell${
          value.finished ? ' progress-meter-finished' : ''
        }`}
        aria-hidden="true"
      >
        <div
          className="progress-meter-fill"
          style={{ '--progress-fill': value.percent / 100 }}
        />
      </div>
    </>
  )
}

MetricCell.propTypes = {
  row: PropTypes.object.isRequired,
  metric: PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
    .isRequired,
}

const metricColumn = metric => ({
  key: metric.key,
  label: metric.label,
  type: 'text',
  exportable: false,
  render: row => <MetricCell row={row} metric={metric} />,
})

// Columns in order: the name, Status, Due, Reading, Videos, Assessments, Average score, and Learning
// activities when the course has simulations.
export const chaptersColumns = ({ level, metrics, noun = 'Chapter' }) => {
  const byKey = key => metrics.find(metric => metric.key === key)
  const course = level === 'course'
  return [
    {
      key: 'title',
      label: course ? noun : 'Section',
      type: 'text',
      required: true,
      value: row => row.title,
      // the course view's chapter rows expand in place, so the name is plain text on every view
      render: row => row.title,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'text',
      value: row => row.status && row.status.label,
      render: row =>
        row.status ? (
          <span className={`me-badge-tint me-badge-tint-${row.status.tone}`}>
            {row.status.label}
          </span>
        ) : (
          <NoData context={`Status for ${row.short}, `} />
        ),
    },
    {
      key: 'due',
      label: 'Due',
      type: 'text',
      value: row => row.due && row.due.date,
      render: row =>
        row.due ? row.due.date : <NoData context={`Due for ${row.short}, `} />,
    },
    ...['reading', 'videos', 'assessments']
      .map(byKey)
      .filter(Boolean)
      .map(metricColumn),
    {
      key: 'score',
      label: 'Average score',
      type: 'text',
      value: row => row.score,
      render: row => (row.score === null ? <NoData /> : `${row.score}%`),
    },
    ...['learning_activities'].map(byKey).filter(Boolean).map(metricColumn),
  ]
}

// A chapter row's Sections rows, fetched when it first opens and shown in the Chapters table's own columns.
export const loadSections = row =>
  row.sectionsUrl
    ? client.get(row.sectionsUrl).then(response => response.data.rows)
    : []

const SECTIONS = {
  load: loadSections,
  noun: 'sections',
  emptyText: 'No sections to show',
}

export const ChaptersTable = ({ data }) => {
  const course = data.level === 'course'
  // the book's own word for a chapter (course/views/progress.py chapter_noun)
  const noun = data.noun || 'Chapter'
  const config = {
    tableId: 'progress-chapters',
    embedded: true,
    caption: course ? `${noun}s` : 'Sections',
    rowKey: 'id',
    columns: chaptersColumns(data),
    paged: false,
    source: { mode: 'client', max: 10000 },
    emptyState: {
      title: `No ${noun.toLowerCase()}s to show`,
      description: `This course has no ${noun.toLowerCase()}s with tracked content.`,
    },
  }
  return (
    <div className="progress-chapters">
      <MeTable
        config={config}
        rows={data.rows}
        status="ready"
        childRows={course ? SECTIONS : null}
      />
    </div>
  )
}

ChaptersTable.propTypes = {
  data: PropTypes.shape({
    level: PropTypes.oneOf(['course', 'chapter']).isRequired,
    metrics: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
  }).isRequired,
}
